import { EPaymentPlan } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { Help } from '@if/mui'
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import { TermAndPlanOption } from '@src/types/TermPlanOptionDto'
import { EditPersonalLoanWorksheetDto } from '@src/types/WorksheetSchema'
import { ReactNode, useCallback, useId, useMemo } from 'react'
import { Control, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const getInvalidTermReasonKey = (
  reason?: string,
): 'worksheet.errors.pmtTooHigh' | 'worksheet.errors.aprTooHighTerm' | undefined => {
  if (reason === 'pmtTooHigh') return 'worksheet.errors.pmtTooHigh'
  if (reason === 'aprTooHigh') return 'worksheet.errors.aprTooHighTerm'
  return undefined
}

const RateCell = ({ option }: { option: TermAndPlanOption }): ReactNode => (
  <Typography variant="body2" sx={{ minWidth: 72, textAlign: 'right' }} noWrap>
    {`${option.promoRate}%`}
  </Typography>
)

const OptionRow = ({ option, hasPromoRows }: { option: TermAndPlanOption; hasPromoRows: boolean }): ReactNode => {
  const { t } = useTranslation()
  const showBestDot = !!option.isBestTerm && !option.disabled
  const detail = option.promoDurationMonths
    ? t('worksheet.promoRateDetail', {
        promoRate: option.promoRate,
        regularRate: option.regularRate,
        count: option.promoDurationMonths,
      })
    : undefined
  const reasonKey = option.disabled ? getInvalidTermReasonKey(option.reason) : undefined

  const row = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
      <ListItemText
        sx={{ flex: 1, my: 0 }}
        primary={t('worksheet.termMonths', { count: option.termMonths })}
        secondary={
          hasPromoRows ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {option.planLabel ?? t('worksheet.regularPlan')}
              {detail && <Help title={detail} />}
            </Box>
          ) : undefined
        }
      />
      <Typography variant="body2" sx={{ minWidth: 72, textAlign: 'right' }} noWrap>
        {option.disabled ? '' : formatCurrency(option.monthlyPayment)}
      </Typography>
      <RateCell option={option} />
      <Box sx={{ minWidth: 64, display: 'flex', justifyContent: 'center' }}>
        {option.isDefault && <Chip size="small" label={t('worksheet.termAndPlanDefault')} />}
      </Box>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: showBestDot ? 'success.main' : 'transparent' }} />
    </Box>
  )

  if (!reasonKey) return row
  return (
    <Tooltip title={t(reasonKey)} placement="right">
      <Box component="span" sx={{ display: 'flex', width: '100%', pointerEvents: 'auto' }}>
        {row}
      </Box>
    </Tooltip>
  )
}

interface Props {
  control: Control<EditPersonalLoanWorksheetDto>
  options: TermAndPlanOption[]
  hasPromoRows: boolean
  isLoading: boolean
  label?: string
  disabled?: boolean
}

const TermAndPlanSelect = ({ control, options, hasPromoRows, isLoading, label, disabled }: Props) => {
  const { t } = useTranslation()
  const fieldLabel = label ?? t('worksheet.termAndPlan')
  const labelId = useId()

  const { field: planField } = useController({ control, name: 'paymentPlanId' })
  const { field: termField } = useController({ control, name: 'term' })

  const currentKey = termField.value != null ? `${planField.value}|${termField.value}` : ''
  const value = options.some((o) => o.key === currentKey) ? currentKey : ''

  const menuItems = useMemo(
    () =>
      options.map((o) => (
        <MenuItem key={o.key} value={o.key} disabled={o.disabled}>
          <OptionRow option={o} hasPromoRows={hasPromoRows} />
        </MenuItem>
      )),
    [options, hasPromoRows],
  )

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const option = options.find((o) => o.key === event.target.value)
      if (!option || option.disabled) return
      planField.onChange(option.paymentPlanId as EPaymentPlan)
      termField.onChange(option.termMonths)
    },
    [options, planField, termField],
  )

  const renderValue = useCallback(
    (selected: string): ReactNode => {
      const option = options.find((o) => o.key === selected)
      if (!option) return null
      const subtitle = hasPromoRows ? ` · ${option.planLabel ?? t('worksheet.regularPlan')}` : ''
      return `${t('worksheet.termMonths', { count: option.termMonths })}${subtitle}`
    },
    [options, hasPromoRows, t],
  )

  if (isLoading) {
    return <Skeleton variant="rounded" height={56} />
  }

  return (
    <FormControl fullWidth>
      <InputLabel id={labelId}>{fieldLabel}</InputLabel>
      <Select
        labelId={labelId}
        label={fieldLabel}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        renderValue={renderValue}
      >
        {menuItems.length > 0 ? menuItems : <MenuItem value="" disabled />}
      </Select>
    </FormControl>
  )
}

export default TermAndPlanSelect
